import {
  createBrowserRouter,
  Navigate,
  type RouteObject,
} from "react-router-dom";

import { RotaProtegida } from "./RotaProtegida";
import { Shell } from "../ui/layout/Shell";
import { CadastrosLayout } from "../ui/cadastros/CadastrosLayout";
import { Login } from "../ui/paginas/Login";
import { Dashboard } from "../ui/paginas/Dashboard";
import { Projecao } from "../ui/paginas/Projecao";
import { Conciliacao } from "../ui/paginas/Conciliacao";
import { TelaDesfazarConciliacao } from "../ui/paginas/TelaDesfazarConciliacao";
import { Trilha } from "../ui/paginas/Trilha";
import { HistoricoAlertas } from "../ui/paginas/HistoricoAlertas";
import { RegrasCanais } from "../ui/paginas/RegrasCanais";
import { ReceitaManual } from "../ui/paginas/ReceitaManual";
import { PaginaEmpresas } from "../ui/paginas/PaginaEmpresas";
import { PaginaContas } from "../ui/paginas/PaginaContas";
import { PaginaBancos } from "../ui/paginas/PaginaBancos";
import { PaginaUsuarios } from "../ui/paginas/PaginaUsuarios";
import { PaginaPerfis } from "../ui/paginas/PaginaPerfis";

/*
 * Router do painel (FE-0 CP-D / FE-1 CP2 — ADR 0026 §Roteamento / React Router v7).
 *
 * Rotas:
 *  - `/login` — tela de login local (PÚBLICA). Destino do sinal de sessão
 *    expirada do interceptor; já-logado se auto-redireciona para `/`.
 *  - `/` (e descendentes) — PROTEGIDA por `<RotaProtegida>` (guard reativo do
 *    zustand): sem token → `/login`. Dentro do guard, o layout `<Shell>` envolve
 *    o `<Outlet>`; o índice `/` é o `<Dashboard>` (Tela 1).
 *
 * O `router` de `createBrowserRouter` expõe `.navigate()` FORA do React. O
 * bootstrap (`main.tsx`) usa isso para plugar a navegação real do interceptor
 * via `configurarSessaoExpirada` — fechamento (a) do CP-C.
 *
 * Factory para teste determinístico (cada teste cria um router isolado). Em
 * produção, `main.tsx` cria um único router.
 *
 * As rotas vivem em `rotasDoPainel` (exportadas) para que os testes possam
 * montar a MESMA árvore num memory-router declarativo (o data-router de
 * `createBrowserRouter` com `.navigate()` esbarra no AbortSignal do jsdom).
 */
export const rotasDoPainel: RouteObject[] = [
  {
    path: "/login",
    element: <Login />,
  },
  {
    // Guard reativo: tudo sob `/` exige sessão ativa.
    path: "/",
    element: <RotaProtegida />,
    children: [
      {
        element: <Shell />,
        children: [
          { index: true, element: <Dashboard /> },
          { path: "projecao", element: <Projecao /> },
          { path: "conciliacao", element: <Conciliacao /> },
          {
            path: "conciliacao/desfazer",
            element: <TelaDesfazarConciliacao />,
          },
          { path: "trilha", element: <Trilha /> },
          { path: "historico/alertas", element: <HistoricoAlertas /> },
          { path: "regras", element: <RegrasCanais /> },
          { path: "receitas/manual", element: <ReceitaManual /> },
          {
            path: "cadastros",
            element: <CadastrosLayout />,
            children: [
              // Index do módulo redireciona para a seção Empresas.
              { index: true, element: <Navigate to="empresas" replace /> },
              { path: "empresas", element: <PaginaEmpresas /> },
              {
                path: "empresas/:empresaId/contas",
                element: <PaginaContas />,
              },
              { path: "bancos", element: <PaginaBancos /> },
              { path: "usuarios", element: <PaginaUsuarios /> },
              { path: "perfis", element: <PaginaPerfis /> },
            ],
          },
        ],
      },
    ],
  },
];

export function criarRouter(): ReturnType<typeof createBrowserRouter> {
  return createBrowserRouter(rotasDoPainel);
}
